import { validateForm } from "./helper";

export const lobOptions = ["Motor", "Health"];

export const lobPayloadMap = {
  Motor: 1,
  Health: 2,
};

export const clubOptions = ["Gold", "Silver", "Bronze", "Platinum"];

export const companySizeOptions = ["Medium", "Large"];

export const emptyFormData = {
  company_name: "",
  pan_number: "",
  gc_code: "",
  gst_number: "",
  state: "",
  city: "",
  club: "",
  company_size: "",
  specialised: "no",
  lob_id: "",
  sub_product_id: [],
  mobile_number: "",
  email: "",
  capacity_motor: "",
  capacity_health: "",
  high_value: [],
  employee_name: "",
  employee_mobile: "",
  employee_email: "",
};

export const validateEditVendorForm = (form) => {
  const errors = validateForm(form);
  const employeeMobile = String(form?.employee_mobile || "").trim();
  if (!employeeMobile) {
    errors.employee_mobile = "Employee Mobile is required.";
  } else if (!/^[6-9][0-9]{9}$/.test(employeeMobile)) {
    errors.employee_mobile = "Enter valid 10 digit mobile number.";
  }
  return errors;
};

export const LOB = {
  MOTOR: "Motor",
  HEALTH: "Health",
};

export const LOB_ID = {
  [LOB.MOTOR]: 1,
  [LOB.HEALTH]: 2,
};

export const SPECIALISED_OPTIONS = ["Yes", "No"];

export const HIGH_VALUE = {
  NO_HV: "NO HV",
  MOTOR_OD: "OD HV",
  MOTOR_TP: "TP HV",
  HEALTH: "Health HV",
};

export const UPDATED_BY = "IM";

export const STATUS = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

export const getVendorLob = (vendorDetails) => {
  const isMotor =
    vendorDetails?.is_motor === 1 ||
    vendorDetails?.is_motor === "1";
  const isHealth =
    vendorDetails?.is_health === 1 ||
    vendorDetails?.is_health === "1";
  if (isMotor) return LOB.MOTOR;
  if (isHealth) return LOB.HEALTH;
  return "";
};

export const getInitialHighValue = (vendorDetails, selectedLob) => {
  if (selectedLob === LOB.MOTOR) {
    const values = [
      ...(vendorDetails?.od_hv === 1 ? [HIGH_VALUE.MOTOR_OD] : []),
      ...(vendorDetails?.tp_hv === 1 ? [HIGH_VALUE.MOTOR_TP] : []),
    ];
    return values.length ? values : [HIGH_VALUE.NO_HV];
  }

  if (selectedLob === LOB.HEALTH) {
    return vendorDetails?.health_hv === 1
      ? [HIGH_VALUE.HEALTH]
      : [HIGH_VALUE.NO_HV];
  }
  return [];
};

export const getSelectedSubProducts = (
  subProducts,
  vendorSubProductIds = []
) => {
  return subProducts
    .filter((subProduct) =>
      vendorSubProductIds.includes(subProduct.sub_product_id)
    )
    .map((subProduct) => subProduct.sub_product_name);
};

export const getInitialFormData = ({
  vendorDetails,
  selectedLob,
  subProducts,
}) => ({
  ...emptyFormData,
  company_name: vendorDetails?.company_name || "",
  pan_number: (vendorDetails?.pan_number || "").toUpperCase(),
  gc_code: vendorDetails?.gc_code || "",
  gst_number: (vendorDetails?.gst_number || "").toUpperCase(),
  state: vendorDetails?.state || "",
  city: vendorDetails?.city || "",
  status:
    vendorDetails?.isActive === true ||
    vendorDetails?.isActive === "true"
      ? STATUS.ACTIVE
      : STATUS.INACTIVE,
  club: vendorDetails?.club || "",
  company_size: vendorDetails?.company_size || "",
  specialised:
    vendorDetails?.specialised === 1 ||
    vendorDetails?.specialised === "1"
      ? "yes"
      : "no",
  lob_id: selectedLob,
  sub_product_id: getSelectedSubProducts(
    subProducts,
    vendorDetails?.sub_product_id
  ),
  mobile_number: vendorDetails?.employee_mobile || "",
  email: vendorDetails?.employee_email || "",
  capacity_motor: vendorDetails?.capacity_motor ?? "",
  capacity_health: vendorDetails?.capacity_health ?? "",
  high_value: getInitialHighValue(vendorDetails, selectedLob),
  employee_name: vendorDetails?.employee_name || "",
  employee_mobile: vendorDetails?.employee_mobile || "",
  employee_email: vendorDetails?.employee_email || "",
});

export const getHighValueOptions = ({
  lob,
  subProducts,
}) => {
  if (lob === LOB.MOTOR) {
    if (!subProducts.length) return [];
    return [
      ...subProducts.map(
        (subProduct) => `${subProduct} HV`
      ),
      HIGH_VALUE.NO_HV,
    ];
  }
  if (lob === LOB.HEALTH) {
    return [
      HIGH_VALUE.HEALTH,
      HIGH_VALUE.NO_HV,
    ];
  }
  return [];
};

export const getNextHighValue = ({
  rawValue,
  availableOptions,
  previousValue,
}) => {
  const rawValues =
    typeof rawValue === "string"
      ? rawValue.split(",")
      : rawValue || [];

  const addedValues = rawValues.filter(
    (value) => !previousValue.includes(value)
  );
  const addedNoHv = addedValues.includes(HIGH_VALUE.NO_HV);
  const addedHv = addedValues.some(
    (value) => value !== HIGH_VALUE.NO_HV
  );
  let nextValues = rawValues.filter((value) =>
    availableOptions.includes(value)
  );
  if (addedNoHv) {
    nextValues = [HIGH_VALUE.NO_HV];
  } else if (addedHv) {
    nextValues = nextValues.filter(
      (value) => value !== HIGH_VALUE.NO_HV
    );
  }
  return nextValues;
};

export const getUpdatedSubProducts = ({
  value,
  previousSubProducts,
  availableOptions,
}) => {
  if (value === LOB.HEALTH) {
    return [];
  }
  return previousSubProducts.filter((subProduct) =>
    availableOptions.includes(subProduct)
  );
};

export const buildVendorUpdatePayload = ({
  vendorId,
  formData,
  fetchedSubProducts,
  lobPayloadMap,
}) => {
  const isMotor = formData.lob_id === LOB.MOTOR;
  const isHealth = formData.lob_id === LOB.HEALTH;
  const subProductIds = isMotor
    ? formData.sub_product_id
        .map((name) => {
          const subProduct = fetchedSubProducts.find(
            (item) => item.sub_product_name === name
          );
          return subProduct?.sub_product_id;
        })
        .filter(Boolean)
    : [];
  return {
    vendor_company_id: vendorId,
    company_name: formData.company_name,
    pan_number: formData.pan_number || "",
    gst_number: formData.gst_number || "",
    state: formData.state,
    city: formData.city,
    lob_id: lobPayloadMap[formData.lob_id] || "",
    club: formData.club,
    specialised: formData.specialised === "yes" ? 1 : 0,
    company_size: formData.company_size,
    sub_product_id: subProductIds,
    mobile_number: formData.employee_mobile,
    email: formData.employee_email,
    capacity_motor: isMotor
      ? Number(formData.capacity_motor || 0)
      : 0,
    capacity_health: isHealth
      ? Number(formData.capacity_health || 0)
      : 0,
    od_hv:
      isMotor &&
      formData.high_value.includes(HIGH_VALUE.MOTOR_OD)
        ? 1
        : 0,
    tp_hv:
      isMotor &&
      formData.high_value.includes(HIGH_VALUE.MOTOR_TP)
        ? 1
        : 0,
    health_hv:
      isHealth &&
      formData.high_value.includes(HIGH_VALUE.HEALTH)
        ? 1
        : 0,
    updated_by: UPDATED_BY,
  };
};

export const validateVendorForm = (data) => {
  const errors = {};
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i;
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
  const mobileRegex = /^[6-9]\d{9}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const numRegex = /^\d+$/;
  if (!data.company_name?.trim()) errors.company_name = "Company name is required";
  if (!data.gc_code?.trim()) errors.gc_code = "GC Code is required";
  if (!data.state?.trim()) errors.state = "State is required";
  if (!data.city?.trim()) errors.city = "City is required";
  if (!data.status?.trim()) errors.status = "Status is required";
  if (!data.club?.trim()) errors.club = "Club is required";
  if (!data.company_size?.trim()) errors.company_size = "Company size is required";
  if (!data.specialised?.trim()) errors.specialised = "Specialised is required";
  if (!data.lob_id?.trim()) errors.lob_id = "LOB is required";
  if (data.lob_id === LOB.MOTOR && (!data.sub_product_id || data.sub_product_id.length === 0)) {
    errors.sub_product_id = "Sub Product is required";
  }
  if (!data.high_value || data.high_value.length === 0) {
    errors.high_value = "High Value selection is required";
  }
  if (!data.pan_number?.trim()) {
    errors.pan_number = "PAN number is required";
  } else if (!panRegex.test(data.pan_number.trim())) {
    errors.pan_number = "Invalid PAN number format";
  }
  if (data.gst_number?.trim() && !gstRegex.test(data.gst_number.trim())) {
    errors.gst_number = "Invalid GST number format";
  }
  if (data.lob_id === LOB.MOTOR) {
    if (!data.capacity_motor?.toString().trim()) {
      errors.capacity_motor = "Capacity is required";
    } else if (!numRegex.test(data.capacity_motor)) {
      errors.capacity_motor = "Capacity must be a valid number";
    }
  }
  if (data.lob_id === LOB.HEALTH) {
    if (!data.capacity_health?.toString().trim()) {
      errors.capacity_health = "Capacity is required";
    } else if (!numRegex.test(data.capacity_health)) {
      errors.capacity_health = "Capacity must be a valid number";
    }
  }
  if (!data.employee_name?.trim()) errors.employee_name = "Employee name is required";
  if (!data.employee_mobile?.trim()) {
    errors.employee_mobile = "Mobile number is required";
  } else if (!mobileRegex.test(data.employee_mobile.trim())) {
    errors.employee_mobile = "Invalid 10-digit mobile number";
  }
  if (!data.employee_email?.trim()) {
    errors.employee_email = "Email is required";
  } else if (!emailRegex.test(data.employee_email.trim())) {
    errors.employee_email = "Invalid email address";
  }
  return errors;
};